import React, { useEffect, useRef } from 'react';
import { Box, Dialog, DialogActions, DialogContent, IconButton, Slide } from '@mui/material';
import type { SlideProps } from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import { getDashboardFlyerUrl } from '../utils/dashboardFlyerUrl';

export interface DashboardPost {
  title?: string;
  body?: string;
  imageUrl?: string;
  htmlUrl?: string;
  updatedAt: string | Date | null;
}

const SlideDownTransition = React.forwardRef<unknown, SlideProps>(function SlideDownTransition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface DashboardAnnouncementDialogProps {
  open: boolean;
  post: DashboardPost | null;
  onClose: () => void;
  onNavigate?: (path: string) => void;
}

const DashboardAnnouncementDialog: React.FC<DashboardAnnouncementDialogProps> = ({ open, post, onClose, onNavigate }) => {
  const flyerFrame = useRef<HTMLIFrameElement>(null);
  const flyerUrl = post?.htmlUrl ? getDashboardFlyerUrl(post.htmlUrl) : '';

  useEffect(() => {
    if (!open || !flyerUrl) return undefined;
    const handleFlyerMessage = (event: MessageEvent) => {
      if (event.source !== flyerFrame.current?.contentWindow) return;
      if (event.data === 'dashboard-announcement:close') {
        onClose();
        return;
      }
      const requestedPath = event.data?.type === 'dashboard-announcement:navigate'
        ? event.data.path
        : '';
      if (requestedPath === '/profile#referral-code' || requestedPath === '/payment') {
        onClose();
        onNavigate?.(requestedPath);
      }
    };
    window.addEventListener('message', handleFlyerMessage);
    return () => window.removeEventListener('message', handleFlyerMessage);
  }, [open, onClose, onNavigate, flyerUrl]);

  if (!post || (!post.imageUrl && !post.htmlUrl)) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideDownTransition}
      maxWidth="md"
      fullWidth
      aria-label="Dashboard announcement"
      PaperProps={{
        sx: {
          maxWidth: 760,
          overflow: 'hidden',
          borderRadius: { xs: 2, sm: 3 },
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.28)',
        },
      }}
    >
      <DialogContent sx={{ p: 0, bgcolor: '#101828', overflowY: 'auto' }}>
        {flyerUrl ? (
          <Box
            component="iframe"
            ref={flyerFrame}
            src={flyerUrl}
            title="Dashboard announcement flyer"
            sandbox="allow-scripts allow-top-navigation-by-user-activation"
            referrerPolicy="no-referrer"
            sx={{ display: 'block', width: '100%', height: { xs: 'min(72vh, 620px)', sm: 'min(72vh, 680px)' }, border: 0, bgcolor: 'background.paper' }}
          />
        ) : (
          <Box
            component="img"
            src={post.imageUrl}
            alt="Dashboard announcement"
            sx={{ display: 'block', width: '100%', maxHeight: 'calc(100vh - 144px)', objectFit: 'contain' }}
          />
        )}
        {(post.title || post.body) && (
          <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 }, bgcolor: 'background.paper' }}>
            {post.title && (
              <Box component="h2" sx={{ m: 0, mb: post.body ? 1 : 0, fontSize: { xs: '1.05rem', sm: '1.2rem' }, fontWeight: 700, lineHeight: 1.35, color: 'text.primary' }}>
                {post.title}
              </Box>
            )}
            {post.body && (
              <Box component="p" sx={{ m: 0, color: 'text.secondary', fontSize: { xs: '0.95rem', sm: '1rem' }, lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                {post.body}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', px: 2, py: 1.25 }}>
        <IconButton onClick={onClose} aria-label="Close announcement" size="large">
          <CloseIcon />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardAnnouncementDialog;